'use client';

import { fitToViewport } from '@ps/functions/media/fitToViewport';
import { AlertCircle, Play } from 'lucide-react';
import { PhotoView } from 'react-photo-view';

import { LightboxVideo } from './LightboxVideo';

/** Stage aspect used until the video's real dimensions have been measured. */
const kFallbackStageWidth = 1280;
const kFallbackStageHeight = 720;

interface BaseProps {
  filename: string;
  contentType: string;
  /** Image src or video src — local object URL or remote CDN URL. */
  src: string;
}

interface RemoteProps extends BaseProps {
  kind: 'remote';
  /** This tile's index in the message's gallery — gates the video's load/play. */
  index: number;
  /** Measured intrinsic video dimensions, used to fit the lightbox stage. */
  videoWidth?: number;
  videoHeight?: number;
}

interface LocalProps extends BaseProps {
  kind: 'local';
  /** Upload progress in `[0, 1]`, or `undefined` once the upload finishes. */
  progress?: number;
  /** Populated when the upload failed; renders the error overlay. */
  errorMessage?: string;
}

type Props = RemoteProps | LocalProps;

const TILE_CLASS =
  'bg-surface-1 relative block h-[120px] w-[120px] shrink-0 overflow-hidden rounded-md';

/**
 * One attachment in a channel-message bubble. Renders as a square tile with the
 * media as `object-cover`. Video files show a play overlay. Local (in-flight)
 * attachments overlay a circular progress ring while uploading; errors get a red
 * alert badge.
 *
 * A remote IMAGE or VIDEO opens the full-screen `react-photo-view` lightbox —
 * one shared, swipeable gallery per message (the strip wraps its tiles in a
 * `PhotoProvider`). Images use the built-in zoom/pan; videos are rendered via
 * `PhotoView`'s `render` escape hatch as an inline `<video controls>`. Local
 * tiles aren't interactive until the upload confirms.
 */
export function ChannelMessageAttachmentTile(props: Props) {
  const isVideo = props.contentType.startsWith('video/');
  const showProgress =
    props.kind === 'local' &&
    props.progress != null &&
    props.progress < 1 &&
    props.errorMessage == null;
  const showError = props.kind === 'local' && props.errorMessage != null;

  const inner = (
    <>
      {isVideo ? (
        <>
          <video
            src={props.src}
            className="h-full w-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
          <div className="bg-scrim/30 absolute inset-0 flex items-center justify-center">
            <Play className="h-7 w-7 fill-white text-white" aria-hidden />
          </div>
        </>
      ) : (
        <img
          src={props.src}
          alt={props.filename}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      )}
      {showProgress ? (
        <div className="bg-scrim/35 absolute inset-0 flex items-center justify-center">
          <CircularProgress progress={(props as LocalProps).progress ?? 0} />
        </div>
      ) : null}
      {showError ? (
        <div
          className="bg-scrim/60 absolute bottom-1.5 right-1.5 flex items-center justify-center rounded-full p-1"
          title={(props as LocalProps).errorMessage}
        >
          <AlertCircle className="text-danger h-4 w-4" aria-hidden />
        </div>
      ) : null}
    </>
  );

  if (props.kind === 'remote') {
    // Both open the shared lightbox. `PhotoView` clones the trigger button to
    // open the slider (animating from the tile's rect); the enclosing
    // `PhotoProvider` (in the strip) makes the message's media one gallery.
    const trigger = (
      <button type="button" className={`${TILE_CLASS} cursor-zoom-in`}>
        {inner}
      </button>
    );
    if (isVideo) {
      // Videos aren't images, so use `PhotoView`'s `render` escape hatch. A
      // custom render slide is drawn at the raw width/height at scale 1 (unlike
      // images, react-photo-view doesn't fit it), so pass the real aspect ratio
      // fitted to the viewport — otherwise oversized dimensions crop. Falls back
      // to a landscape box until the video's dimensions have been measured.
      // {@link LightboxVideo} is `object-contain` within the stage and
      // loads/plays only while it's the active slide.
      const stage =
        props.videoWidth && props.videoHeight
          ? fitToViewport(props.videoWidth, props.videoHeight)
          : { width: kFallbackStageWidth, height: kFallbackStageHeight };
      return (
        <PhotoView
          width={stage.width}
          height={stage.height}
          render={({ attrs }) => (
            <LightboxVideo attrs={attrs} src={props.src} index={props.index} />
          )}
        >
          {trigger}
        </PhotoView>
      );
    }
    return <PhotoView src={props.src}>{trigger}</PhotoView>;
  }

  return <div className={TILE_CLASS}>{inner}</div>;
}

function CircularProgress({ progress }: { progress: number }) {
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, progress));
  const offset = circumference * (1 - clamped);
  return (
    <svg viewBox="0 0 40 40" className="h-10 w-10 -rotate-90">
      <circle
        cx={20}
        cy={20}
        r={radius}
        fill="none"
        stroke="rgba(255, 255, 255, 0.25)"
        strokeWidth={3}
      />
      <circle
        cx={20}
        cy={20}
        r={radius}
        fill="none"
        stroke="white"
        strokeWidth={3}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-[stroke-dashoffset] duration-200"
      />
    </svg>
  );
}
